import React, { useState, useEffect } from 'react';
import { useTheme } from '../../theme/ThemeProvider';
import { newtonProtocolsAPI } from '../../api';
import ChartElement from './elements/ChartElement';
import TableElement from './elements/TableElement';
import MetricElement from './elements/MetricElement';

const ProtocolViewer = ({ protocol, dataSources, onBack, onEdit }) => {
  const { mode } = useTheme();
  const [executionData, setExecutionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [hasExecuted, setHasExecuted] = useState(false);
  
  const layoutConfig = protocol.layout_config || { elements: [] };
  const elements = layoutConfig.elements || [];
  
  // NO ejecutar automáticamente - proteger servidor
  // El usuario debe hacer click en "Ejecutar" manualmente
  
  const executeProtocol = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Llamar al endpoint de ejecución con timeout
      const response = await newtonProtocolsAPI.execution.execute(protocol.id, {}, 1000);
      
      if (response.success) {
        setExecutionData(response);
        setHasExecuted(true);
        
        // Mostrar si vino de caché
        if (response.from_cache) {
          console.log('⚡ Resultado obtenido desde caché');
        }
      } else {
        setError(response.error || 'Error ejecutando protocolo');
      }
    } catch (err) {
      console.error('Error executing protocol:', err);
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Timeout: El protocolo tardó demasiado. Intenta reducir la cantidad de datos.');
      } else if (err.response?.status === 504) {
        setError('Timeout del servidor (30s). El protocolo es muy grande. Contacta al administrador.');
      } else {
        setError(err.response?.data?.detail || err.message || 'Error desconocido');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const renderElement = (element) => {
    // Obtener datos procesados del backend para este elemento
    const elementData = executionData?.elements?.[element.id];
    
    const commonProps = {
      element,
      processedData: elementData,
      isEditing: false
    };
    
    switch (element.type) {
      case 'chart':
        return <ChartElement {...commonProps} />;
      case 'table':
        return <TableElement {...commonProps} />;
      case 'metric':
        return <MetricElement {...commonProps} />;
      default:
        return <div>Tipo de elemento no soportado</div>;
    }
  };
  
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: fullscreen ? '100vh' : 'calc(100vh - 100px)',
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-primary)' : '#F5F7FA',
      position: fullscreen ? 'fixed' : 'relative',
      top: fullscreen ? 0 : 'auto',
      left: fullscreen ? 0 : 'auto',
      right: fullscreen ? 0 : 'auto',
      bottom: fullscreen ? 0 : 'auto',
      zIndex: fullscreen ? 9999 : 'auto'
    },
    header: {
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-secondary)' : 'var(--color-white)',
      borderBottom: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
      padding: 'var(--spacing-lg) var(--spacing-xl)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 'var(--spacing-md)'
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--spacing-lg)',
      flex: 1,
      minWidth: '300px'
    },
    backButton: {
      padding: 'var(--spacing-sm) var(--spacing-md)',
      backgroundColor: 'transparent',
      border: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
      borderRadius: 'var(--border-radius-md)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)',
      fontSize: 'var(--font-size-base)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--spacing-xs)'
    },
    title: {
      fontSize: 'var(--font-size-2xl)',
      fontWeight: 'var(--font-weight-bold)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)',
      margin: 0
    },
    subtitle: {
      fontSize: 'var(--font-size-sm)',
      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)',
      marginTop: 'var(--spacing-xs)'
    },
    headerRight: {
      display: 'flex',
      gap: 'var(--spacing-sm)',
      flexWrap: 'wrap'
    },
    canvas: {
      flex: 1,
      padding: 'var(--spacing-xl)',
      overflowY: 'auto',
      overflowX: 'hidden'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(12, 1fr)',
      gap: 'var(--spacing-md)',
      gridAutoRows: '60px',
      minHeight: '800px'
    },
    element: {
      backgroundColor: mode === 'dark' ? 'var(--dark-bg-secondary)' : 'var(--color-white)',
      borderRadius: 'var(--border-radius-lg)',
      border: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
      padding: 'var(--spacing-md)',
      overflow: 'hidden',
      boxShadow: mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)'
    },
    elementHeader: {
      fontSize: 'var(--font-size-base)',
      fontWeight: 'var(--font-weight-semibold)',
      color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)',
      marginBottom: 'var(--spacing-sm)',
      paddingBottom: 'var(--spacing-sm)',
      borderBottom: `1px solid ${mode === 'dark' ? 'var(--dark-border-primary)' : 'var(--color-light-gray)'}`,
    },
    elementBody: {
      height: 'calc(100% - 48px)',
      overflow: 'auto'
    },
    loadingOverlay: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '400px',
      fontSize: 'var(--font-size-lg)',
      color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)'
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '400px',
      gap: 'var(--spacing-md)',
      color: mode === 'dark' ? 'var(--dark-text-tertiary)' : 'var(--color-medium-gray)'
    }
  };
  
  // Estado inicial: No ejecutado
  if (!hasExecuted && !loading && !error) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <button style={styles.backButton} onClick={onBack}>
              ← Volver
            </button>
            <div>
              <h1 style={styles.title}>{protocol.name}</h1>
              {protocol.description && (
                <p style={styles.subtitle}>{protocol.description}</p>
              )}
            </div>
          </div>
          <div style={styles.headerRight}>
            <button
              className="btn btn-secondary"
              onClick={onEdit}
            >
              ✏️ Editar
            </button>
          </div>
        </div>
        <div style={styles.loadingOverlay}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>📊</div>
            <h2 style={{ color: mode === 'dark' ? 'var(--dark-text-primary)' : 'var(--color-black)', marginBottom: '16px' }}>
              Protocolo Listo para Ejecutar
            </h2>
            <p style={{ color: mode === 'dark' ? 'var(--dark-text-secondary)' : 'var(--color-medium-gray)', marginBottom: '32px', maxWidth: '500px', margin: '0 auto 32px' }}>
              Este protocolo tiene {elements.length} elementos. Haz clic en "Ejecutar" para cargar los datos.
              {executionData?.from_cache && <><br/>✨ Los datos están en caché - carga instantánea</>}
            </p>
            <button
              className="btn btn-primary"
              onClick={executeProtocol}
              style={{ fontSize: '18px', padding: '16px 32px' }}
            >
              ▶️ Ejecutar Protocolo
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <button style={styles.backButton} onClick={onBack}>
              ← Volver
            </button>
            <div>
              <h1 style={styles.title}>{protocol.name}</h1>
            </div>
          </div>
        </div>
        <div style={styles.loadingOverlay}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <div style={{ fontSize: '18px', marginBottom: '8px' }}>Ejecutando protocolo...</div>
          <div style={{ fontSize: '14px', opacity: 0.7 }}>Esto puede tardar hasta 30 segundos</div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <button style={styles.backButton} onClick={onBack}>
              ← Volver
            </button>
            <div>
              <h1 style={styles.title}>{protocol.name}</h1>
            </div>
          </div>
        </div>
        <div style={styles.loadingOverlay}>
          <div style={{ color: '#FF6B35', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <div style={{ fontSize: '18px', marginBottom: '8px' }}>Error ejecutando protocolo</div>
            <div style={{ fontSize: '14px', opacity: 0.8 }}>{error}</div>
            <button 
              className="btn btn-primary" 
              onClick={executeProtocol}
              style={{ marginTop: '24px' }}
            >
              🔄 Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button 
            style={styles.backButton} 
            onClick={onBack}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = mode === 'dark' ? 'var(--dark-bg-tertiary)' : 'var(--color-light-gray)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            ← Volver
          </button>
          <div>
            <h1 style={styles.title}>👁️ {protocol.name}</h1>
            {protocol.description && (
              <p style={styles.subtitle}>{protocol.description}</p>
            )}
          </div>
        </div>
        
        <div style={styles.headerRight}>
          <button
            className="btn btn-ghost"
            onClick={executeProtocol}
            title="Refrescar datos"
            disabled={loading}
          >
            🔄 Refrescar
          </button>
          <button
            className="btn btn-ghost"
            onClick={() => setFullscreen(!fullscreen)}
            title={fullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
          >
            {fullscreen ? '🗗' : '⛶'} {fullscreen ? 'Normal' : 'Pantalla Completa'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={onEdit}
            title="Editar este protocolo"
          >
            ✏️ Editar
          </button>
        </div>
        
        {/* Indicador de caché */}
        {executionData?.from_cache && (
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            padding: '4px 12px',
            backgroundColor: '#38C172',
            color: '#FFFFFF',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            ⚡ Caché
          </div>
        )}
      </div>
      
      {/* Canvas con elementos */}
      <div style={styles.canvas}>
        {elements.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ fontSize: '64px' }}>📊</div>
            <div>Este protocolo no tiene elementos configurados</div>
          </div>
        ) : (
          <div style={styles.grid}>
            {elements.map((element) => {
              const gridColumn = `span ${element.position.w}`;
              const gridRow = `span ${element.position.h}`;
              
              return (
                <div
                  key={element.id}
                  style={{
                    ...styles.element,
                    gridColumn,
                    gridRow
                  }}
                >
                  <div style={styles.elementHeader}>
                    {element.config?.title || 'Sin título'}
                  </div>
                  <div style={styles.elementBody}>
                    {renderElement(element)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProtocolViewer;



